import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Building2, Home, Video, MessageCircle, Calendar as CalendarIcon, Clock, MapPin, ShieldCheck } from 'lucide-react';
import { useAppContext } from '../contexts/AppContext';
export function BookingScreen() {
  const navigate = useNavigate();
  const {
    selectedProfessional,
    setBookingData
  } = useAppContext();
  const [step, setStep] = useState(1);
  const [careType, setCareType] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  // Redirect if no professional selected
  if (!selectedProfessional) {
    return <div className="min-h-screen flex items-center justify-center p-6 text-center" data-id="element-172">
        <div data-id="element-173">
          <p className="mb-4" data-id="element-174">No professional selected.</p>
          <button onClick={() => navigate('/explore')} className="text-brand-primary font-bold" data-id="element-175">
            Go to Explore
          </button>
        </div>
      </div>;
  }
  const careTypes = [{
    id: 'Clinic',
    icon: Building2,
    title: 'Clinic Visit',
    desc: 'Visit the doctor at their clinic'
  }, {
    id: 'Home Visit',
    icon: Home,
    title: 'Home Visit',
    desc: 'Doctor comes to your location'
  }, {
    id: 'Video',
    icon: Video,
    title: 'Video Consult',
    desc: 'Online video call'
  }, {
    id: 'Chat',
    icon: MessageCircle,
    title: 'Chat Consult',
    desc: 'Text-based consultation'
  }].filter(type => selectedProfessional.modes.includes(type.id as any));
  const dates = ['Today, Oct 12', 'Tomorrow, Oct 13', 'Wed, Oct 14', 'Thu, Oct 15'];
  const times = ['09:00 AM', '10:30 AM', '01:00 PM', '02:30 PM', '04:00 PM'];
  const handleContinue = () => {
    if (step === 1) setStep(2);else if (step === 2) setStep(3);else {
      setBookingData({
        professional: selectedProfessional,
        careType,
        date: selectedDate,
        time: selectedTime,
        fee: selectedProfessional.fee,
        platformFee: 1000
      });
      navigate('/payment');
    }
  };
  return <div className="min-h-screen bg-ui-bg flex flex-col" data-id="element-176">
      {/* Header */}
      <div className="pt-12 px-6 pb-4 bg-white shadow-sm z-10" data-id="element-177">
        <div className="flex items-center mb-4" data-id="element-178">
          <button onClick={() => step > 1 ? setStep(step - 1) : navigate(-1)} className="p-2 -ml-2 rounded-full hover:bg-ui-card" data-id="element-179">
            <ArrowLeft size={24} className="text-text-main" data-id="element-180" />
          </button>
          <h1 className="text-xl font-bold text-text-main ml-2" data-id="element-181">
            Book Appointment
          </h1>
        </div>

        {/* Progress */}
        <div className="flex space-x-2" data-id="element-182">
          {[1, 2, 3].map(i => <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= step ? 'bg-brand-primary' : 'bg-ui-border'}`} data-id="element-183" />)}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6" data-id="element-184">
        {/* Professional Summary Card */}
        <div className="bg-white p-4 rounded-2xl border border-ui-border shadow-sm mb-6 flex items-center space-x-4" data-id="element-185">
          <img src={selectedProfessional.image} alt={selectedProfessional.name} className="w-16 h-16 rounded-xl object-cover" data-id="element-186" />
          <div data-id="element-187">
            <div className="flex items-center" data-id="element-188">
              <h3 className="font-bold text-text-main" data-id="element-189">
                {selectedProfessional.name}
              </h3>
              {selectedProfessional.verified && <ShieldCheck size={14} className="text-brand-primary ml-1" data-id="element-190" />}
            </div>
            <p className="text-sm text-text-secondary" data-id="element-191">
              {selectedProfessional.specialty}
            </p>
            <p className="text-sm font-bold text-brand-primary mt-1" data-id="element-192">
              ₦{selectedProfessional.fee.toLocaleString()}
            </p>
          </div>
        </div>

        {/* Step 1: Care Type */}
        {step === 1 && <div className="animate-fade-in space-y-4" data-id="element-193">
            <h2 className="text-lg font-bold text-text-main mb-2" data-id="element-194">
              Select Care Type
            </h2>
            {careTypes.map(type => <button key={type.id} onClick={() => setCareType(type.id)} className={`w-full flex items-center p-4 rounded-2xl border-2 transition-all text-left ${careType === type.id ? 'border-brand-primary bg-brand-light' : 'border-ui-border bg-white hover:border-brand-primary/30'}`} data-id="element-195">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center mr-4 ${careType === type.id ? 'bg-brand-primary text-white' : 'bg-ui-card text-text-secondary'}`} data-id="element-196">
                  <type.icon size={24} data-id="element-197" />
                </div>
                <div data-id="element-198">
                  <h3 className="font-bold text-text-main" data-id="element-199">{type.title}</h3>
                  <p className="text-sm text-text-secondary" data-id="element-200">{type.desc}</p>
                </div>
              </button>)}
          </div>}

        {/* Step 2: Date & Time */}
        {step === 2 && <div className="animate-fade-in space-y-6" data-id="element-201">
            <div data-id="element-202">
              <h2 className="text-lg font-bold text-text-main mb-3 flex items-center" data-id="element-203">
                <CalendarIcon size={20} className="mr-2 text-brand-primary" data-id="element-204" />{' '}
                Select Date
              </h2>
              <div className="flex overflow-x-auto no-scrollbar space-x-3 pb-2 -mx-6 px-6" data-id="element-205">
                {dates.map(date => <button key={date} onClick={() => setSelectedDate(date)} className={`min-w-[120px] p-3 rounded-xl border-2 text-center transition-all ${selectedDate === date ? 'border-brand-primary bg-brand-primary text-white' : 'border-ui-border bg-white text-text-main'}`} data-id="element-206">
                    <div className="text-xs opacity-80 mb-1" data-id="element-207">
                      {date.split(',')[0]}
                    </div>
                    <div className="font-bold" data-id="element-208">{date.split(',')[1]}</div>
                  </button>)}
              </div>
            </div>

            <div data-id="element-209">
              <h2 className="text-lg font-bold text-text-main mb-3 flex items-center" data-id="element-210">
                <Clock size={20} className="mr-2 text-brand-primary" data-id="element-211" /> Select
                Time
              </h2>
              <div className="grid grid-cols-3 gap-3" data-id="element-212">
                {times.map(time => <button key={time} onClick={() => setSelectedTime(time)} className={`py-3 rounded-xl border-2 text-sm font-medium transition-all ${selectedTime === time ? 'border-brand-primary bg-brand-light text-brand-primary' : 'border-ui-border bg-white text-text-main'}`} data-id="element-213">
                    {time}
                  </button>)}
              </div>
            </div>

            {careType === 'Home Visit' && <div className="mt-6" data-id="element-214">
                <h2 className="text-lg font-bold text-text-main mb-3 flex items-center" data-id="element-215">
                  <MapPin size={20} className="mr-2 text-brand-primary" data-id="element-216" />{' '}
                  Confirm Address
                </h2>
                <div className="bg-white p-4 rounded-xl border border-ui-border" data-id="element-217">
                  <p className="font-medium" data-id="element-218">14 Admiralty Way</p>
                  <p className="text-sm text-text-secondary" data-id="element-219">
                    Lekki Phase 1, Lagos
                  </p>
                  <button className="text-brand-primary text-sm font-medium mt-2" data-id="element-220">
                    Change Address
                  </button>
                </div>
              </div>}
          </div>}

        {/* Step 3: Summary */}
        {step === 3 && <div className="animate-fade-in space-y-6" data-id="element-221">
            <h2 className="text-lg font-bold text-text-main" data-id="element-222">
              Booking Summary
            </h2>

            <div className="bg-white rounded-2xl border border-ui-border overflow-hidden" data-id="element-223">
              <div className="p-4 border-b border-ui-border bg-ui-card flex justify-between items-center" data-id="element-224">
                <div className="flex items-center text-text-main font-medium" data-id="element-225">
                  {careType === 'Video' ? <Video size={18} className="mr-2 text-brand-primary" data-id="element-226" /> : careType === 'Home Visit' ? <Home size={18} className="mr-2 text-brand-primary" data-id="element-227" /> : <Building2 size={18} className="mr-2 text-brand-primary" data-id="element-228" />}
                  {careType} Consultation
                </div>
              </div>
              <div className="p-4 space-y-3" data-id="element-229">
                <div className="flex justify-between" data-id="element-230">
                  <span className="text-text-secondary" data-id="element-231">Date & Time</span>
                  <span className="font-medium text-right" data-id="element-232">
                    {selectedDate}
                    <br data-id="element-233" />
                    {selectedTime}
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-234">
                  <span className="text-text-secondary" data-id="element-235">Patient</span>
                  <span className="font-medium" data-id="element-236">Oluwaseun Adebayo (Self)</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-ui-border p-4 space-y-3" data-id="element-237">
              <h3 className="font-bold text-text-main mb-2" data-id="element-238">Payment Details</h3>
              <div className="flex justify-between text-sm" data-id="element-239">
                <span className="text-text-secondary" data-id="element-240">Consultation Fee</span>
                <span data-id="element-241">₦{selectedProfessional.fee.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm" data-id="element-242">
                <span className="text-text-secondary" data-id="element-243">Platform Fee</span>
                <span data-id="element-244">₦1,000</span>
              </div>
              <div className="pt-3 border-t border-ui-border flex justify-between font-bold text-lg" data-id="element-245">
                <span data-id="element-246">Total</span>
                <span className="text-brand-primary" data-id="element-247">
                  ₦{(selectedProfessional.fee + 1000).toLocaleString()}
                </span>
              </div>
            </div>

            <div className="bg-brand-light p-4 rounded-xl flex items-start" data-id="element-248">
              <ShieldCheck size={20} className="text-brand-primary mr-3 shrink-0 mt-0.5" data-id="element-249" />
              <p className="text-xs text-brand-dark leading-relaxed" data-id="element-250">
                Your payment is held securely by Whiz and only released to the
                professional after your consultation is completed.
              </p>
            </div>
          </div>}
      </div>

      {/* Footer CTA */}
      <div className="p-6 bg-white border-t border-ui-border" data-id="element-251">
        <button onClick={handleContinue} disabled={step === 1 && !careType || step === 2 && (!selectedDate || !selectedTime)} className="w-full bg-brand-primary text-white font-semibold py-4 rounded-xl shadow-md disabled:opacity-50 transition-all" data-id="element-252">
          {step === 3 ? 'Continue to Payment' : 'Continue'}
        </button>
      </div>
    </div>;
}